import React, { useEffect, useState, useContext } from 'react';
import {
  Alert, Button, Descriptions, Input, Popconfirm, Segmented, Select, Spin, Table, Tag, Typography, Upload, message,
} from 'antd';
import { InboxOutlined, CloudUploadOutlined, IdcardOutlined } from '@ant-design/icons';
import api from '../api';
import { UserContext, hasGrant } from '../App';

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;

export default function CNEMonthlyStatsUploadPage() {
  const currentUser = useContext(UserContext);
  // Role, or an explicit grant for this page. The grant is additive, so it
  // opens the page for someone outside the CNE team and never closes it.
  const isAdmin     = currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin'
                   || (currentUser?.JobID === 6 && currentUser?.TeamID === 3)
                   || hasGrant(currentUser, 'cne-upload');

  const [view, setView] = useState('upload');

  // ── Upload view state ────────────────────────────────────────────────────────
  const [monthKeys,        setMonthKeys]        = useState([]);
  const [monthKeysLoading, setMonthKeysLoading] = useState(true);
  const [selectedMonth,    setSelectedMonth]    = useState(null);
  const [file,             setFile]             = useState(null);
  const [preview,          setPreview]          = useState(null);
  const [previewing,       setPreviewing]       = useState(false);
  const [assignments,      setAssignments]      = useState({});
  const [saving,           setSaving]           = useState(false);
  const [result,           setResult]           = useState(null);
  const [existing,         setExisting]         = useState([]);
  const [existingLoading,  setExistingLoading]  = useState(false);

  // ── AWS ID editor state ──────────────────────────────────────────────────────
  const [teamUsers,        setTeamUsers]        = useState([]);
  const [teamUsersLoading, setTeamUsersLoading] = useState(false);
  const [awsIdDraft,       setAwsIdDraft]       = useState({});
  const [savingIds,        setSavingIds]        = useState(new Set());

  // ── Load month keys once ─────────────────────────────────────────────────────
  useEffect(() => {
    api.get('/api/admin/cne-month-keys')
      .then((r) => setMonthKeys(r.data))
      .catch(() => {})
      .finally(() => setMonthKeysLoading(false));
  }, []);

  // ── Load existing stats when month selected ──────────────────────────────────
  useEffect(() => {
    if (!selectedMonth) { setExisting([]); return; }
    setExistingLoading(true);
    setResult(null);
    api.get('/api/admin/cne-monthly-stats', { params: { month_key: selectedMonth } })
      .then((r) => setExisting(r.data))
      .catch(() => setExisting([]))
      .finally(() => setExistingLoading(false));
  }, [selectedMonth]);

  // ── Load team users when AWS ID view is opened ───────────────────────────────
  useEffect(() => {
    if (view !== 'aws-ids') return;
    setTeamUsersLoading(true);
    api.get('/api/admin/cne-team-users')
      .then((r) => {
        setTeamUsers(r.data);
        const draft = {};
        r.data.forEach((u) => { draft[u.ID] = u.AWSID || ''; });
        setAwsIdDraft(draft);
      })
      .catch(() => message.error('Failed to load team users.'))
      .finally(() => setTeamUsersLoading(false));
  }, [view]);

  // ── Upload logic ─────────────────────────────────────────────────────────────
  // Preview then commit, the same as the daily upload: the export cannot say
  // which campaign an agent belongs to, so one is suggested and confirmed
  // before anything is written.
  const doPreview = async (f) => {
    setPreviewing(true);
    setPreview(null);
    setResult(null);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const res = await api.post('/api/admin/cne-upload/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      const seed = {};
      (res.data.agents || []).forEach((a) => { seed[a.AWSID] = a.SuggestedCampaign; });
      setAssignments(seed);
    } catch (err) {
      message.error(err.response?.data?.error || 'Could not read that file.');
      setFile(null);
    } finally {
      setPreviewing(false);
    }
  };

  const doCommit = async () => {
    if (!selectedMonth) { message.warning('Select a month first.'); return; }
    setSaving(true);
    const fd = new FormData();
    fd.append('month_key', selectedMonth);
    fd.append('file', file);
    fd.append('assignments', JSON.stringify(assignments));
    try {
      const res = await api.post('/api/admin/cne-upload/commit', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      message.success(`Saved ${res.data.inserted} rows for ${selectedMonth}.`);
      setPreview(null);
      setFile(null);
      try {
        const updated = await api.get('/api/admin/cne-monthly-stats', { params: { month_key: selectedMonth } });
        setExisting(updated.data);
      } catch { /* non-fatal */ }
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const campaignOptions = (preview?.campaigns || []).map((c) => ({ label: c.Name, value: c.Name }));
  const changedCount = (preview?.agents || [])
    .filter((a) => assignments[a.AWSID] && assignments[a.AWSID] !== a.SuggestedCampaign).length;

  const previewCols = [
    { title: 'Agent', key: 'agent', width: 240,
      render: (_, a) => (
        <span>
          {a.DisplayName
            ? <><strong>{a.DisplayName}</strong>{a.IsActive === false && <Tag color="default" style={{ marginLeft: 6 }}>Inactive</Tag>}
                <br /><Text type="secondary" style={{ fontSize: 11 }}>{a.AWSID}</Text></>
            : <Text type="warning">{a.AWSID} <Tag color="orange">No match</Tag></Text>}
        </span>
      ),
    },
    { title: 'Campaign', key: 'campaign', width: 210,
      render: (_, a) => (
        <div>
          <Select
            size="small"
            style={{ width: 170 }}
            options={campaignOptions}
            value={assignments[a.AWSID]}
            onChange={(v) => setAssignments((prev) => ({ ...prev, [a.AWSID]: v }))}
          />
          {assignments[a.AWSID] !== a.SuggestedCampaign
            ? <div style={{ fontSize: 11, color: '#fa8c16' }}>changed from {a.SuggestedCampaign}</div>
            : <div style={{ fontSize: 11, color: '#8c8c8c' }}>{a.SuggestedFromFile ? 'from the file' : 'from their job'}</div>}
        </div>
      ),
    },
    { title: 'Contacts Handled', dataIndex: 'ContactsHandled', key: 'ContactsHandled', width: 140, align: 'right' },
    { title: 'Avg Handle (hrs)', dataIndex: 'AvgHandleHrs', key: 'AvgHandleHrs', width: 140, align: 'right',
      render: (v) => v != null ? Number(v).toFixed(4) : '—' },
    { title: 'Occupancy %', dataIndex: 'OccupancyPct', key: 'OccupancyPct', width: 110, align: 'right',
      render: (v) => v != null ? `${Number(v).toFixed(2)}%` : '—' },
  ];

  // ── AWS ID save ──────────────────────────────────────────────────────────────
  const saveAwsId = async (userId) => {
    setSavingIds((prev) => new Set([...prev, userId]));
    try {
      // The CNE-scoped endpoint, not the general user PUT: that one needs
      // admin or CanEditUsers, so a page grant alone could open this tab and
      // then fail on every Save.
      await api.put('/api/admin/cne-aws-id', { user_id: userId, aws_id: awsIdDraft[userId] || null });
      message.success('AWS ID updated.');
      setTeamUsers((prev) => prev.map((u) =>
        u.ID === userId ? { ...u, AWSID: awsIdDraft[userId] || null } : u
      ));
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to update AWS ID.');
    } finally {
      setSavingIds((prev) => { const s = new Set(prev); s.delete(userId); return s; });
    }
  };

  // ── Table columns ────────────────────────────────────────────────────────────
  const existingCols = [
    { title: 'Agent', key: 'agent', width: 200,
      render: (_, r) => (
        <span>
          {r.DisplayName
            ? <><strong>{r.DisplayName}</strong><br /><Text type="secondary" style={{ fontSize: 11 }}>{r.AWSID}</Text></>
            : <Text type="warning">{r.AWSID} <Tag color="orange">No match</Tag></Text>}
        </span>
      ),
    },
    { title: 'Campaign', dataIndex: 'CampaignName', key: 'CampaignName', width: 130 },
    { title: 'Contacts Handled', dataIndex: 'ContactsHandled', key: 'ContactsHandled', width: 140, align: 'right' },
    { title: 'Avg Handle (hrs)', dataIndex: 'AvgHandleHrs', key: 'AvgHandleHrs', width: 140, align: 'right',
      render: (v) => v != null ? Number(v).toFixed(4) : '—' },
    { title: 'Occupancy %', dataIndex: 'OccupancyPct', key: 'OccupancyPct', width: 110, align: 'right',
      render: (v) => v != null ? `${Number(v).toFixed(2)}%` : '—' },
    { title: 'Uploaded', dataIndex: 'UploadedAt', key: 'UploadedAt', width: 160,
      render: (v) => v ? v.replace('T', ' ').substring(0, 16) : '—' },
  ];

  const awsIdCols = [
    { title: 'Name', key: 'name', width: 220,
      render: (_, r) => (
        <span>
          {r.DisplayName}
          {!r.IsActive && <Tag color="default" style={{ marginLeft: 8 }}>Inactive</Tag>}
        </span>
      ),
    },
    { title: 'AWS Agent ID', key: 'awsid',
      render: (_, r) => (
        <Input
          size="small"
          value={awsIdDraft[r.ID] ?? ''}
          placeholder="e.g. TSmith@0054N000005Dbc3@00D410000006xvV"
          style={{ fontFamily: 'monospace', fontSize: 12 }}
          onChange={(e) => setAwsIdDraft((prev) => ({ ...prev, [r.ID]: e.target.value }))}
          allowClear
        />
      ),
    },
    { title: '', key: 'save', width: 80,
      render: (_, r) => {
        const changed = (awsIdDraft[r.ID] ?? '') !== (r.AWSID || '');
        return (
          <Button
            size="small"
            type={changed ? 'primary' : 'default'}
            loading={savingIds.has(r.ID)}
            disabled={!changed}
            onClick={() => saveAwsId(r.ID)}
          >
            Save
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>CNE Monthly Stats</Title>
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { label: 'Upload', value: 'upload', icon: <CloudUploadOutlined /> },
            { label: 'Update AWS Agent IDs', value: 'aws-ids', icon: <IdcardOutlined /> },
          ]}
        />
      </div>

      {/* ── UPLOAD VIEW ─────────────────────────────────────────────────────── */}
      {view === 'upload' && (
        <>
          <Paragraph type="secondary" style={{ marginBottom: 20 }}>
            Upload the CNE Historical Metrics MQT export (tab-separated). The file must have an <strong>Agent</strong> column;
            it does not have to carry a campaign. One is suggested per agent — from the file where it is there, otherwise from
            their job — and can be changed before saving. All existing rows for the selected month are replaced. Agents are
            matched via the <strong>AWS ID</strong> field on each user record.
          </Paragraph>

          {!isAdmin && (
            <Alert type="warning" message="Admin access required to upload data." style={{ marginBottom: 16 }} />
          )}

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
            <Text strong>Month:</Text>
            {monthKeysLoading
              ? <Spin size="small" />
              : (
                <Select
                  style={{ minWidth: 160 }}
                  placeholder="Select month"
                  value={selectedMonth}
                  onChange={setSelectedMonth}
                  options={monthKeys.map((k) => ({ label: k, value: k }))}
                  showSearch
                />
              )
            }
          </div>

          {isAdmin && !preview && (
            <Dragger
              name="file"
              accept=".txt,.tsv,.csv"
              beforeUpload={(f) => { setFile(f); doPreview(f); return false; }}
              onRemove={() => { setFile(null); setPreview(null); }}
              fileList={file ? [file] : []}
              disabled={previewing}
              style={{ marginBottom: 16 }}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">Click or drag a tab-delimited .txt file here</p>
              <p className="ant-upload-hint">
                Must have an &quot;Agent&quot; column. Pagination rows are ignored. Nothing is saved until you confirm the campaigns on the next step.
              </p>
            </Dragger>
          )}

          {previewing && <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>}

          {preview && (
            <>
              <Alert
                type={preview.unmatched?.length ? 'warning' : 'info'}
                style={{ marginBottom: 16 }}
                message={preview.unmatched?.length
                  ? `${preview.unmatched.length} agent(s) have no matching AWS ID in QAUsers`
                  : 'Ready to save'}
                description={
                  <>
                    <Descriptions size="small" column={2} style={{ marginTop: 8 }}>
                      <Descriptions.Item label="Agents">{preview.row_count}</Descriptions.Item>
                      <Descriptions.Item label="Month">{selectedMonth || <Text type="danger">not selected</Text>}</Descriptions.Item>
                    </Descriptions>
                    {preview.unmatched?.length > 0 && (
                      <>
                        <p style={{ margin: '8px 0 4px' }}>
                          These save anyway and link the moment their AWS ID is set — use the
                          <strong> Update AWS Agent IDs</strong> tab.
                        </p>
                        <div style={{ maxHeight: 140, overflowY: 'auto', fontFamily: 'monospace', fontSize: 12, background: 'rgba(0,0,0,0.04)', padding: '8px 12px', borderRadius: 4 }}>
                          {preview.unmatched.map((id) => <div key={id}>{id}</div>)}
                        </div>
                      </>
                    )}
                  </>
                }
              />

              <Title level={5} style={{ marginBottom: 8 }}>
                Confirm campaigns
                {changedCount > 0 && (
                  <Text type="secondary" style={{ fontWeight: 400, marginLeft: 8 }}>
                    {changedCount} changed from the suggestion
                  </Text>
                )}
              </Title>
              <Table
                rowKey="AWSID"
                dataSource={preview.agents}
                columns={previewCols}
                size="small"
                pagination={false}
                scroll={{ x: 'max-content' }}
                bordered
                style={{ marginBottom: 16 }}
              />

              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                <Popconfirm
                  title={`Save ${preview.row_count} rows for ${selectedMonth || '—'}?`}
                  description="Everything already stored for this month is replaced."
                  okText="Save"
                  disabled={!selectedMonth}
                  onConfirm={doCommit}
                >
                  <Button type="primary" loading={saving} disabled={!selectedMonth}>
                    Save {preview.row_count} Rows
                  </Button>
                </Popconfirm>
                <Button onClick={() => { setPreview(null); setFile(null); }} disabled={saving}>
                  Cancel
                </Button>
              </div>
            </>
          )}

          {result && (
            <Alert
              type="success"
              style={{ marginBottom: 20 }}
              message={`Saved for ${selectedMonth}`}
              description={
                <Descriptions size="small" column={3} style={{ marginTop: 8 }}>
                  <Descriptions.Item label="Rows saved">{result.inserted}</Descriptions.Item>
                  <Descriptions.Item label="Matched to user">{result.matched}</Descriptions.Item>
                  <Descriptions.Item label="Unmatched">
                    {result.unmatched > 0
                      ? <Tag color="orange">{result.unmatched} — use AWS Agent IDs tab to link</Tag>
                      : <Tag color="green">0</Tag>}
                  </Descriptions.Item>
                </Descriptions>
              }
            />
          )}

          {selectedMonth && (
            <>
              <Title level={5} style={{ marginBottom: 8 }}>
                Uploaded rows for {selectedMonth}
                <Text type="secondary" style={{ fontWeight: 400, marginLeft: 8 }}>
                  {existing.length > 0 ? `${existing.length} agents` : ''} (showing existing entries if any)
                </Text>
              </Title>
              {existingLoading
                ? <Spin />
                : existing.length === 0
                  ? <Text type="secondary">No data uploaded for this month yet.</Text>
                  : (
                    <Table
                      rowKey="ID"
                      dataSource={existing}
                      columns={existingCols}
                      size="small"
                      pagination={false}
                      scroll={{ x: 'max-content' }}
                      bordered
                    />
                  )
              }
            </>
          )}
        </>
      )}

      {/* ── AWS ID EDITOR VIEW ───────────────────────────────────────────────── */}
      {view === 'aws-ids' && (
        <>
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Set the <strong>AWS Agent ID</strong> for each CNE team member. This links uploaded monthly stats to the correct user. The Save button activates when a value has been changed.
          </Paragraph>

          {teamUsersLoading
            ? <Spin />
            : (
              <Table
                rowKey="ID"
                dataSource={teamUsers}
                columns={awsIdCols}
                size="small"
                pagination={false}
                bordered
                rowClassName={(r) => !r.IsActive ? 'ant-table-row-muted' : ''}
              />
            )
          }
        </>
      )}
    </>
  );
}
